import React, {useState} from 'react';
import {useSelector, useDispatch} from 'react-redux';

import {IconoirTrash} from '@ey-xd/motif-icon';

import OutputText from '@ey/canvascoreservices/OutputText';

import {
	userDoesNotHavePermissionOnAllEntitiesErrorCode,
	newUserDoesNotHavePermissionOnAllEntitiesErrorCode,
	anotherAdministratorAlreadyReviewedAccessForUserErrorCode,
	newAnotherAdministratorAlreadyReviewedAccessForUserErrorCode,
	cannotRemovePOWithNonPOUser
} from '../../../../../util/uiconstants';
import Utility from '../../../../../util/utilityfunctions';
import {labels, currentResource} from '../../../../../util/utils';

import columns from '../../../../Common/ContactTable/columns';
import CommonContactTable from '../../../../Common/ContactTable';

import {clearErrors} from '../../../../../actions/erroractions';
import {deleteContacts} from '../../../../../actions/ContactDB';
import {raiseJSValidationError} from '../../../../../actions/erroractions';
import {setIsModalOpen} from '../../../../../actions/AccessGroups/SetIsModalOpen';
import {updateDisplayData} from '../../../../../actions/Metrics/dashboardContextAction';

import Modal from '../../../../CoreWrappers/Modal';
import normalizeContactList from '../GenerateMessage/GMForm/utils/normalizeContactList';
import getAvailableEntityList from '../GenerateMessage/GMForm/utils/getAvailableEntityList';

import ToolbarButton from '../ToolbarButton';

import normalizeRemoveContact from './normalizeRemoveContact';

import StyledRemoveContacts from './StyledRemoveContacts';

const RemoveContact = () => {
	const contactListStore = useSelector(({contacts}) =>
		contacts?.entities?.length > 0 ? normalizeContactList(getAvailableEntityList(contacts.entities, false)) : []
	);
	const contactsToRemoveList = useSelector((store) => store.contacts.selected);
	const engagements = useSelector(({dashboardContext}) => dashboardContext?.engagements);
	const entities = useSelector(({dashboardContext}) => dashboardContext?.entities);
	const orgChart = useSelector(({dashboardContext}) => dashboardContext?.orgChart);
	const userPreferences = useSelector((store) => store.userPreferences);

	const projectId = Utility.getProjectIdFromUrl();

	const [open, setOpen] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const dispatch = useDispatch();

	const setModalErrors = (val) => dispatch(clearErrors()).then(() => dispatch(setIsModalOpen(val)));

	const displayCustomError = (error, users) => {
		setIsLoading(false);
		const errorCode = error?.response?.data?.errors?.Error[0]?.split('-')[0].trim() || '';
		let customErrorCode = newUserDoesNotHavePermissionOnAllEntitiesErrorCode;
		if (errorCode == anotherAdministratorAlreadyReviewedAccessForUserErrorCode)
			customErrorCode = newAnotherAdministratorAlreadyReviewedAccessForUserErrorCode;

		if (
			errorCode == userDoesNotHavePermissionOnAllEntitiesErrorCode ||
			errorCode == anotherAdministratorAlreadyReviewedAccessForUserErrorCode ||
			errorCode == cannotRemovePOWithNonPOUser
		) {
			const resouceLabel = currentResource.Errors[errorCode];

			let userList = [];
			users?.forEach((user) => userList.push(`${user.name} ${user.lastName}`));
			userList = userList.sort();
			let names = '';
			if (errorCode == cannotRemovePOWithNonPOUser) {
				names = error?.response?.data?.errors?.Error[0]
					?.split('-')[1]
					.split(':')[1]
					.trim();
			} else {
				userList?.forEach((user, i) => {
					if (i === userList.length - 1) {
						names = names + user;
					} else {
						names = names + user + ', ';
					}
				});
			}

			const message = `<strong>${customErrorCode}</strong> - ${resouceLabel.replace(
				'{0}',
				`<strong>${names}</strong>`
			)}`;

			dispatch(raiseJSValidationError(message));
		}
	};

	const renderTrigger = () => {
		const handlerOnOpenClick = () => {
			setOpen(true);
			setModalErrors(true);
		};

		const props = {
			label: labels.toolbarRemove,
			svg: IconoirTrash,
			onClick: handlerOnOpenClick
		};

		return <ToolbarButton {...props} />;
	};

	const setUserList = () => {
		const userSelection = [];
		const contactList = contactListStore;
		const selectedContacts = contactsToRemoveList;
		selectedContacts.forEach((x) => {
			let exist = userSelection?.find((j) => j.userId === x);
			if (!exist) {
				let selected = contactList?.find((j) => j.userId === x);
				let entities = [];
				if (selected) {
					selected.entities.forEach((a) => {
						let entity = {entityId: a.entityId, name: a.label};
						entities.push(entity);
					});
					selected.entities = entities;
					userSelection.push(selected);
				}
			}
		});
		return userSelection;
	};

	const setPayload = () => {
		const selectedContacts = setUserList();

		const entities = [];
		selectedContacts.flatMap((c) => {
			c.entities.forEach((x) => {
				let exist = entities?.find((e) => e.entityId === x.entityId);
				if (!exist) {
					const entity = {entityId: x.entityId, contactIds: [c.userId]};
					entities.push(entity);
				} else {
					entities[entities.map((en) => en.entityId).lastIndexOf(exist.entityId)].contactIds.push(c.userId);
				}
			});
		});

		return entities;
	};

	const renderInstructionalText = () => {
		return (
			<OutputText body1 light>
				{labels.removeInstructionalText}
			</OutputText>
		);
	};

	const renderSelectedContacts = () => {
		return (
			<OutputText h6 regular>
				{labels.removeSelectedContacts}
			</OutputText>
		);
	};

	const renderTable = () => {
		const data = setUserList().map((contact) => ({...contact, id: contact.userId}));
		const props = {
			tableConfig: {
				data,
				columns: columns(labels).removeContacts,
				pagination: true,
				labels: {
					emptyMessage: labels.thereIsNoDataToDisplay,
					show: labels.show,
					first: labels.firstPage,
					last: labels.lastPage,
					previous: labels.prevPage,
					next: labels.nextPage,
					yesLabel: labels.yes,
					noLabel: labels.no,
					scrollColNext: labels.viewMoreColumns,
					scrollColPrev: labels.viewMoreColumns
				},
				className: 'contact-table'
			}
		};

		return <CommonContactTable {...props} />;
	};

	const renderModal = () => {
		const modalProps = {
			showButtons: true,
			show: open,
			title: labels.removeContactTitle,
			closeTitle: labels.closeTitleModal,
			closeBtnTitle: labels.cancelLabel,
			confirmBtnTitle: labels.confirm,
			tootipPosition: 'top',
			hideConfirmButton: false,
			modalsize: 'medium',
			isOkButtonDisabled: isLoading,
			isCancelButtonDisabled: isLoading,
			modalbodystyle: 'no-padding',
			onHide: handlerOnHide,
			onOkClick: handlerOnSubmit,
			isLoading
		};

		return (
			<Modal {...modalProps}>
				{open && (
					<StyledRemoveContacts>
						{renderInstructionalText()}
						{renderSelectedContacts()}
						{renderTable()}
					</StyledRemoveContacts>
				)}
			</Modal>
		);
	};

	const handlerOnHide = () => {
		setOpen(false);
		setModalErrors(false);
	};

	const removeDuplicates = (arr) => {
		return arr.filter((item, index) => arr.indexOf(item) === index);
	};

	const handlerOnSubmit = () => {
		const payload = {entity: setPayload()};
		if (payload?.entity.length > 0) {
			let contacts = [];
			const entityId = payload.entity[0].entityId;
			const entityIds = payload.entity.map((e) => {
				const entityContacts = e?.contactIds;
				contacts = [...contacts, ...entityContacts];
				return e.entityId;
			});

			let selectedEntities = payload?.entity?.map((x) => {
				return entities?.find((y) => y.entityId == x.entityId);
			});

			let entityContacts = [];
			selectedEntities?.map((entity) => {
				let isEntityAdmins = entity?.entityAdmins?.some((x) => x.userId == userPreferences.userId);

				if (!isEntityAdmins) {
					let contacts = entity?.contacts;
					entityContacts = [...entityContacts, ...contacts];
				}
			});

			let selectedContacts = payload?.entity?.flatMap((x) => x.contactIds);
			selectedContacts = removeDuplicates(selectedContacts);

			let users = [];
			selectedContacts?.map((contactId) => {
				let contact = entityContacts.find((x) => x.userId == contactId);
				if (contact != undefined) {
					users = [...users, contact];
				}
			});

			setIsLoading(true);
			dispatch(deleteContacts(projectId, entityId, payload))
				.then(() => {
					const newEntities = normalizeRemoveContact(entities, contactsToRemoveList, entityIds);
					dispatch(updateDisplayData(engagements, newEntities, orgChart));
					setIsLoading(false);
					handlerOnHide();
				})
				.catch((error) => displayCustomError(error, users));
		}
	};

	return (
		<>
			{renderTrigger()}
			{renderModal()}
		</>
	);
};

export default RemoveContact;
